import React, { useState, useEffect, useRef } from 'react';
import { Card, Spinner, Form } from 'react-bootstrap';
import { renderHTMLContent } from '../../utils/htmlRenderer';
import './StreamingContentRenderer.css';

// Humanize function to convert snake_case to readable text
const humanizeText = (str) => {
  if (!str) return '';
  // Convert snake_case to Title Case
  return str
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * StreamingContentRenderer Component
 * Reusable component for displaying streaming content with real-time updates
 * 
 * @param {Object} props - Component props
 * @param {string} props.title - Title for the content panel
 * @param {boolean} props.isStreaming - Whether content is currently streaming
 * @param {boolean} props.isGenerating - Whether content is being generated
 * @param {boolean} props.hasFirstChunk - Whether first chunk has been received
 * @param {boolean} props.isStreamingDone - Whether streaming is complete
 * @param {string} props.streamingContent - Raw streaming content for debugging
 * @param {string} props.parsedHTMLContent - Parsed HTML content to render
 * @param {string} props.finalContent - Final content after streaming
 * @param {string} props.initialContent - Initial content to display (e.g., from existing data)
 * @param {string} props.noContentMessage - Message to show when no content
 * @param {Array} props.metafields - Array of metafields to display
 * @param {React.ReactNode} props.children - Additional content to render
 * @param {string} props.className - Additional CSS classes
 * @param {number} props.chunkCount - Number of chunks received
 * @param {number} props.lastChunkTime - Timestamp of last chunk received
 * @param {number} props.streamingStartTime - Timestamp when streaming started
 */
const StreamingContentRenderer = ({
  title = "Generated Content",
  isStreaming = false,
  isGenerating = false,
  isTranslating = false,
  hasFirstChunk = false,
  isStreamingDone = false,
  streamingContent = "",
  parsedHTMLContent = "",
  finalContent = "",
  initialContent = "",
  noContentMessage = "No content generated yet. Fill in the form and click Generate to see results.",
  metafields = [],
  children,
  className = "",
  chunkCount = 0,
  lastChunkTime = null,
  streamingStartTime = null
}) => {
  // Determine what content to display
  const getDisplayContent = () => {
    // Clean and process content before displaying
    const cleanContent = (content) => {
      if (!content || typeof content !== 'string') return content;
      
      // Remove raw JSON artifacts and clean up content
      let cleaned = content
        .replace(/^\{[\s\S]*?"content":\s*"/, '') // Remove JSON prefix
        .replace(/"\s*\}\s*$/, '') // Remove JSON suffix
        .replace(/",\s*"metafields":\s*\}\s*$/, '') // Remove metafields JSON artifact
        .replace(/,\s*"metafields":\s*\}\s*$/, '') // Remove metafields JSON artifact (without quotes)
        .replace(/",\s*"metafields":\s*\}\s*/, '') // Remove metafields JSON artifact anywhere
        .replace(/,\s*"metafields":\s*\}\s*/, '') // Remove metafields JSON artifact anywhere (without quotes)
        .replace(/\\n/g, '\n') // Convert \n to actual newlines
        .replace(/\\"/g, '"') // Convert \" to actual quotes
        .replace(/\\t/g, '\t') // Convert \t to actual tabs
        .replace(/\s+$/, '') // Remove trailing whitespace
        .trim();
      
      // If it looks like HTML, return as-is
      if (/<[^>]+>/.test(cleaned)) {
        return cleaned;
      }
      
      // If it's plain text, wrap in paragraphs
      if (cleaned && !cleaned.includes('<')) {
        return cleaned.split('\n\n').map(paragraph => 
          paragraph.trim() ? `<p>${paragraph.trim()}</p>` : ''
        ).join('');
      }
      
      return cleaned;
    };

    if (parsedHTMLContent && typeof parsedHTMLContent === 'string') {
      return cleanContent(parsedHTMLContent);
    }
    if (finalContent && typeof finalContent === 'string') {
      return cleanContent(finalContent);
    }
    if (initialContent && typeof initialContent === 'string') {
      return cleanContent(initialContent);
    }
    return null;
  };
  const displayContent = getDisplayContent();
  const contentRef = useRef(null);
  const [scriptLoading, setScriptLoading] = useState(false);
  const [scriptError, setScriptError] = useState(false);
  const [currentTime, setCurrentTime] = useState(Date.now());

  // Update current time every second when streaming
  useEffect(() => {
    let interval;
    if (isStreaming && streamingStartTime) {
      interval = setInterval(() => {
        setCurrentTime(Date.now());
      }, 1000);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isStreaming, streamingStartTime]);

  const renderMetafieldValue = (value) => {
    if (!value) return 'No value';
    
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return renderParsedValue(parsed);
      } catch (e) {
        return value;
      }
    }
    
    if (typeof value === 'object') {
      return renderParsedValue(value);
    }
    
    return String(value);
  };

  const renderParsedValue = (parsedValue) => {
    if (Array.isArray(parsedValue)) {
      return (
        <div>
          <strong>Array ({parsedValue.length} items):</strong>
          <ul className="mt-2">
            {parsedValue.map((item, index) => (
              <li key={index}>
                {typeof item === 'object' ? (
                  <pre style={{ fontSize: '12px', margin: '5px 0' }}>
                    {JSON.stringify(item, null, 2)}
                  </pre>
                ) : (
                  String(item)
                )}
              </li>
            ))}
          </ul>
        </div>
      );
    }
    
    if (typeof parsedValue === 'object' && parsedValue !== null) {
      // Check if it's a structured content object (like the one you showed)
      if (parsedValue.type && parsedValue.children) {
        return renderStructuredContent(parsedValue);
      }
      
      // Regular object
      return (
        <div>
          <strong>Object:</strong>
          <pre style={{ fontSize: '12px', margin: '5px 0', maxHeight: '200px', overflow: 'auto' }}>
            {JSON.stringify(parsedValue, null, 2)}
          </pre>
        </div>
      );
    }
    
    return String(parsedValue);
  };

  // Helper function to render structured content (like the JSON you provided)
  const renderStructuredContent = (content) => {
    if (!content || !content.children) return 'Invalid content structure';
    
    const renderNode = (node, index) => {
      if (!node) return null;
      
      switch (node.type) {
        case 'heading':
          const HeadingTag = `h${node.level || 2}`;
          return (
            <HeadingTag key={index} style={{ fontWeight: 'bold', marginTop: '10px', marginBottom: '5px' }}>
              {node.children?.map((child, childIndex) => renderNode(child, childIndex))}
            </HeadingTag>
          );
          
        case 'paragraph':
          return (
            <p key={index} style={{ marginBottom: '10px' }}>
              {node.children?.map((child, childIndex) => renderNode(child, childIndex))}
            </p>
          );
          
        case 'text':
          return (
            <span key={index} style={{ fontWeight: node.bold ? 'bold' : 'normal' }}>
              {node.value}
            </span>
          );
          
        case 'link':
          return (
            <a 
              key={index} 
              href={node.url} 
              target={node.target || '_blank'} 
              rel="noopener noreferrer"
              style={{ 
                color: '#007bff', 
                textDecoration: 'underline',
                fontWeight: node.children?.[0]?.bold ? 'bold' : 'normal'
              }}
            >
              {node.children?.map((child, childIndex) => renderNode(child, childIndex))}
            </a>
          );
          
        case 'list':
          const ListTag = node.ordered ? 'ol' : 'ul';
          return (
            <ListTag key={index} style={{ marginBottom: '10px' }}>
              {node.children?.map((child, childIndex) => renderNode(child, childIndex))}
            </ListTag>
          );
          
        case 'listItem':
          return (
            <li key={index} style={{ marginBottom: '5px' }}>
              {node.children?.map((child, childIndex) => renderNode(child, childIndex))}
            </li>
          );
          
        case 'strong':
        case 'bold':
          return (
            <strong key={index}>
              {node.children?.map((child, childIndex) => renderNode(child, childIndex))}
            </strong>
          );
          
        case 'em':
        case 'italic':
          return (
            <em key={index}>
              {node.children?.map((child, childIndex) => renderNode(child, childIndex))}
            </em>
          );
          
        case 'code':
          return (
            <code key={index} style={{ 
              backgroundColor: '#f1f1f1', 
              padding: '2px 4px', 
              borderRadius: '3px',
              fontFamily: 'monospace'
            }}>
              {node.value || node.children?.map((child, childIndex) => renderNode(child, childIndex))}
            </code>
          );
          
        case 'blockquote':
          return (
            <blockquote key={index} style={{ 
              borderLeft: '4px solid #ddd', 
              margin: '10px 0', 
              paddingLeft: '15px',
              fontStyle: 'italic'
            }}>
              {node.children?.map((child, childIndex) => renderNode(child, childIndex))}
            </blockquote>
          );
          
        default:
          return (
            <div key={index} style={{ fontSize: '12px', color: '#666' }}>
              Unknown node type: {node.type}
              {node.children && (
                <div style={{ marginLeft: '10px' }}>
                  {node.children.map((child, childIndex) => renderNode(child, childIndex))}
                </div>
              )}
            </div>
          );
      }
    };
    
    return (
      <div style={{ border: '1px solid #e0e0e0', padding: '10px', borderRadius: '5px', backgroundColor: '#f9f9f9' }}>
        <strong>Structured Content:</strong>
        <div style={{ marginTop: '10px' }}>
          {content.children?.map((child, index) => renderNode(child, index))}
        </div>
      </div>
    );
  };

  // Handle script execution for content with scripts
  useEffect(() => {
    if (displayContent && typeof displayContent === 'string' && contentRef.current && displayContent.includes('<script')) {
      setScriptLoading(true);
      setScriptError(false);
      
      // Clear existing content
      contentRef.current.innerHTML = '';
      
      // Set the HTML content directly
      contentRef.current.innerHTML = displayContent;
      
      // Execute any scripts after a short delay to ensure DOM is ready
      setTimeout(() => {
        const scripts = contentRef.current.querySelectorAll('script');
        let loadedScripts = 0;
        const totalScripts = scripts.length;
        
        if (totalScripts === 0) {
          setScriptLoading(false);
          return;
        }
        
        scripts.forEach(script => {
          const newScript = document.createElement('script');
          newScript.src = script.src;
          newScript.async = script.async;
          newScript.defer = script.defer;
          newScript.type = script.type || 'text/javascript';
          
          // Add success handler
          newScript.onload = () => {
            loadedScripts++;
            if (loadedScripts === totalScripts) {
              setScriptLoading(false);
            }
          };
          
          // Add error handling
          newScript.onerror = () => {
            console.warn('Failed to load script:', script.src);
            loadedScripts++;
            setScriptError(true);
            if (loadedScripts === totalScripts) {
              setScriptLoading(false);
            }
          };
          
          if (script.innerHTML) {
            newScript.innerHTML = script.innerHTML;
          }
          
          // Append to head for execution
          document.head.appendChild(newScript);
        });
      }, 100);
    }
  }, [displayContent]);

  return (
    <Card className={`streaming-content-renderer ${className}`}>
      <Card.Header>
        <div className="d-flex justify-content-between align-items-center">
          <h4>{title}</h4>
        </div>
      </Card.Header>
      <Card.Body>


        {/* Streaming Content with Chunk Display */}
        {(isStreaming || (isGenerating && hasFirstChunk)) && (
          <div className="streaming-content-container">
            
            {/* Show content as it streams, even if partial */}
            {displayContent ? (
              <div 
                className="streaming-html-content streaming-content"
                dangerouslySetInnerHTML={renderHTMLContent(displayContent)}
              />
            ) : (
              /* Show raw streaming content if no parsed content yet */
              <div className="streaming-raw-content">
                {streamingContent && (
                  <div className="mt-3">
                    <h6>Raw Content Preview:</h6>
                    <pre className="bg-light p-3 rounded" style={{ fontSize: '12px', maxHeight: '200px', overflow: 'auto' }}>
                      {streamingContent.substring(0, 500)}
                      {streamingContent.length > 500 && '...'}
                    </pre>
                  </div>
                )}
              </div>
            )}
                    {/* Show streaming status in header instead of overlay */}
        {isGenerating && !hasFirstChunk && !displayContent && (
          <div className="text-center p-4">
            <div className="d-flex align-items-center justify-content-center">
              <Spinner animation="border" size="sm" className="me-2" variant="primary" />
              <span className="text-muted">
                {isTranslating ? "Starting translation..." : "Starting generation..."}
              </span>
            </div>
          </div>
        )}
          </div>
        )}

        {/* Final Content - Show when we have content and not actively streaming */}
        {displayContent && !isStreaming && (
          <div className="generated-content">
            {typeof displayContent === 'string' && displayContent.includes('<script') ? (
              <div>
                {/* Loading state for script content */}
                {scriptLoading && (
                  <div className="text-center p-3">
                    <Spinner animation="border" size="sm" className="me-2" />
                    <span>Loading interactive content...</span>
                  </div>
                )}
                
                {/* Script error state */}
                {scriptError && (
                  <div className="alert alert-warning">
                    <strong>⚠️ Script Loading Error:</strong> The interactive content failed to load. Try the options below.
                  </div>
                )}
                
                {/* Content container */}
                <div 
                  ref={contentRef}
                  className="generated-html-content"
                  style={{ minHeight: scriptLoading ? '200px' : 'auto' }}
                />
                
                {/* Fallback options */}
                <div className="mt-3 p-3" style={{ background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '5px' }}>
                  <h6 className="text-info">📋 Interactive Content Options</h6>
                  <p className="mb-2">This content contains interactive elements. If it doesn't display properly above, try these options:</p>
                  <div className="d-flex gap-2 flex-wrap">
                    <button 
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => {
                        // Create a new window/tab with the content
                        const newWindow = window.open('', '_blank');
                        newWindow.document.write(`
                          <!DOCTYPE html>
                          <html>
                          <head>
                            <title>FAQ Content</title>
                            <meta charset="utf-8">
                            <meta name="viewport" content="width=device-width, initial-scale=1">
                          </head>
                          <body>
                            ${displayContent}
                          </body>
                          </html>
                        `);
                        newWindow.document.close();
                      }}
                    >
                      Open in New Tab
                    </button>
                    <button 
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => {
                        // Copy to clipboard
                        navigator.clipboard.writeText(displayContent);
                        alert('Content copied to clipboard!');
                      }}
                    >
                      Copy HTML
                    </button>
                    <button 
                      className="btn btn-sm btn-outline-info"
                      onClick={() => {
                        // Reload the scripts
                        setScriptLoading(true);
                        setScriptError(false);
                        const scripts = contentRef.current.querySelectorAll('script');
                        scripts.forEach(script => {
                          const newScript = document.createElement('script');
                          newScript.src = script.src;
                          newScript.async = true;
                          document.head.appendChild(newScript);
                        });
                      }}
                    >
                      Retry Loading
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div 
                className="generated-html-content"
                dangerouslySetInnerHTML={renderHTMLContent(displayContent)}
              />
            )}
          </div>
        )}

        {/* Fallback: Show content if we have it but main condition failed */}
       

        {/* No Content State */}
        {!isGenerating && !isStreaming && !isStreamingDone && !displayContent && (
          <div className="no-content">
            <p>{noContentMessage}</p>
          </div>
        )}

        {metafields && metafields.length > 0 && (
          <div className="mt-4">
            <hr />
            <h5 className="mb-3">Shopify Metafields</h5>
            <div className="metafields-container">
              {metafields.map((metafield, index) => (
                <div key={metafield.key} className="metafield-item mb-3 p-3" style={{
                  background: '#f8f9fa',
                  borderRadius: '5px',
                  border: '1px solid #dee2e6'
                }}>
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div className="flex-grow-1">
                      Key: {humanizeText(metafield.key)}<br />
                      Namespace: {metafield.namespace}<br />
                      Description: {metafield.description}<br />
                    </div>
                  </div>
                  <div>
                   Value: {renderMetafieldValue(metafield.value)}
                  </div>
                  {metafield.metaobjects && Array.isArray(metafield.metaobjects) && metafield.metaobjects.length > 0 && (
                    <div className="mt-3">
                      <h6 className="text-success mb-2">Metaobjects ({metafield.metaobjects.length} items)</h6>
                      <div className="row">
                        {metafield.metaobjects.map((metaobject, metaIndex) => (
                          <div key={metaobject.id || metaIndex} className="col-12 mb-3">
                            <div className="metaobject-card p-3" style={{
                              background: '#f8f9fa',
                              borderRadius: '5px',
                              border: '1px solid #dee2e6'
                            }}>
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <div className="flex-grow-1">
                                  <strong className="text-success">{metaobject.type}</strong>
                                  <small className="text-muted d-block">
                                    Handle: {metaobject.handle} | ID: {metaobject.id}
                                  </small>
                                </div>
                              </div>
                              
                              {metaobject.fields && Array.isArray(metaobject.fields) && (
                                <div className="metaobject-fields">
                                  {metaobject.fields.map((field, fieldIndex) => (
                                    <div key={fieldIndex} className="field-item mb-2">
                                      <div className="d-flex justify-content-between align-items-start">
                                        <strong className="text-dark">{humanizeText(field.key)}:</strong>
                                        <div className="text-primary" style={{ marginLeft: '10px', maxWidth: '70%' }}>
                                          {renderMetafieldValue(field.value)}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Additional Children Content */}
        {children}
      </Card.Body>
    </Card>
  );
};

export default StreamingContentRenderer;
